import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Language, updateLanguage } from "@src/reducers/your-reducer";
import styles from "./dropdown-language.module.scss";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import ukFlag from "@src/assets/flags/uk.svg";
import deFlag from "@src/assets/flags/de.svg";
import cnFlag from "@src/assets/flags/cn.svg";
import itFlag from "@src/assets/flags/it.svg";
import ptBrFlag from "@src/assets/flags/pt-br.svg";
/* import esFlag from "@src/assets/flags/es.svg"; */
import dutchFlag from "@src/assets/flags/nl.svg";
import turkishFlag from "@src/assets/flags/tr.svg";
import { patchUserLanguageUseCase } from "@src/cloud-connect/user-manager/use-cases/patch-user-language-use-case";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";
import { toISO3 } from "@src/cloud-connect/user-manager/utils/to-iso3";

const cssClassName = "dropdown-language";

export const DropDownLanguage = () => {
  const i18n = useI18N();
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.yourSlice.language);
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const languages = [
    {
      code: "en" as Language,
      label: i18n("modules.dropDownLanguage.englishLabel"),
      flag: ukFlag,
    },
    {
      code: "de" as Language,
      label: i18n("modules.dropDownLanguage.germanLabel"),
      flag: deFlag,
    },
    {
      code: "zh" as Language,
      label: i18n("modules.dropDownLanguage.chineseLabel"),
      flag: cnFlag,
    },
    {
      code: "it" as Language,
      label: i18n("modules.dropDownLanguage.italianLabel"),
      flag: itFlag,
    },
    /* {
      code: "es" as Language,
      label: i18n("modules.dropDownLanguage.spanishLabel"),
      flag: esFlag,
    }, */
    {
      code: "pt" as Language,
      label: i18n("modules.dropDownLanguage.portugueseBrazilianLabel"),
      flag: ptBrFlag,
    },
    {
      code: "nl" as Language,
      label: i18n("modules.dropDownLanguage.dutchLabel"),
      flag: dutchFlag,
    },
    {
      code: "tr" as Language,
      label: i18n("modules.dropDownLanguage.turkishLabel"),
      flag: turkishFlag,
    },
  ];

  const [open, setOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState("en");
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const popoverRef = useRef(null);

  useClickOutsideListener(popoverRef, () => setOpen(false), true);

  // Stay in sync if any other part of the app changes the language
  useEffect(() => {
    if (language && language !== selectedCode) {
      setSelectedCode(language);
    }
  }, [language, selectedCode]);

  const handleSelect = async (langCode: Language) => {
    setIsChangingLanguage(true);
    setSelectedCode(langCode);
    /* localStorage.setItem("tt-lang", langCode); */
    setOpen(false);

    if (authContext && authContext.userId) {
      const response = await patchUserLanguageUseCase(
        authContext.userId,
        toISO3(langCode),
      );

      if (!response.success) {
        setSelectedCode(language);
        dispatch(updateLanguage(language as Language));
        errorSnackbar("It was not possible to update your language.");
        setIsChangingLanguage(false);
      } else {
        dispatch(updateLanguage(langCode as Language));
        sessionStorage.setItem("user-language", langCode);
        setIsChangingLanguage(false);
      }
    }
  };

  const selected =
    languages.find((l) => l.code === selectedCode) || languages[0];

  return (
    <div className={styles[cssClassName]} ref={popoverRef}>
      <button
        type="button"
        className={styles[`${cssClassName}__selector`]}
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={selected.flag}
          alt={selected.label}
          className={styles[`${cssClassName}__flag`]}
        />
        {isChangingLanguage ? (
          <SpinnerWithVariant variant={["small", "secondary"]} />
        ) : (
          <span
            className={styles[`${cssClassName}__label`]}
            title={selected.label}
          >
            {selected.label}
          </span>
        )}
      </button>

      {open && (
        <ul
          className={styles[`${cssClassName}__list`]}
          role="listbox"
          aria-activedescendant={selected.code}
          tabIndex={0}
        >
          {languages.map((lang) => (
            <li
              key={lang.code}
              id={lang.code}
              role="option"
              aria-selected={selected.code === lang.code}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__item`]}
                onClick={() => handleSelect(lang.code)}
              >
                <img
                  src={lang.flag}
                  alt={lang.label}
                  className={styles[`${cssClassName}__flag`]}
                />
                {lang.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
